import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  FileText,
  GitPullRequest,
  PencilLine,
  Plus,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import type { DashboardDto } from '@imsenta/shared';
import { PageHeader } from '@/components/AppShell';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Spinner, StatusBadge } from '@/components/ui';
import { api } from '@/lib/api';
import { daysUntil, fmtDate, fmtRelative } from '@/lib/format';
import { useSession } from '@/lib/session';

function Stat({
  label,
  value,
  icon: Icon,
  tone = 'neutral',
  to,
}: {
  label: string;
  value: number;
  icon: typeof FileText;
  tone?: 'neutral' | 'brand' | 'success' | 'warning' | 'danger';
  to?: string;
}) {
  const tones = {
    neutral: 'text-ink-500 bg-ink-100',
    brand: 'text-brand-600 bg-brand-50',
    success: 'text-emerald-600 bg-emerald-50',
    warning: 'text-amber-600 bg-amber-50',
    danger: 'text-rose-600 bg-rose-50',
  };
  const inner = (
    <Card className="flex h-full items-center gap-2.5 p-3 transition-shadow hover:shadow-sm sm:gap-3.5 sm:p-4">
      <span className={`grid size-9 shrink-0 place-items-center rounded-lg sm:size-10 ${tones[tone]}`}>
        <Icon className="size-4.5 sm:size-5" />
      </span>
      <div className="min-w-0">
        <p className="text-xl leading-none font-semibold text-ink-900 sm:text-2xl">{value}</p>
        <p className="mt-1 text-[12px] leading-tight text-ink-500 sm:text-[12.5px]">{label}</p>
      </div>
    </Card>
  );
  return to ? (
    <Link to={to} className="block h-full">
      {inner}
    </Link>
  ) : (
    inner
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { session, can } = useSession();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['dashboard'], queryFn: () => api.get<DashboardDto>('/dashboard') });

  if (isLoading) return <Spinner label="Loading your dashboard…" />;
  if (error || !data) {
    return (
      <Card>
        <ErrorState error={error} onRetry={() => void refetch()} title="Could not load your dashboard" />
      </Card>
    );
  }

  const firstName = session?.user.name.split(' ')[0] ?? 'there';

  return (
    <>
      <PageHeader
        title={`Good day, ${firstName}`}
        description={`Policy library for ${session?.tenant?.name ?? 'your organisation'}.`}
        actions={
          can('document:create') ? (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => navigate('/documents?new=1')}>
              New document
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <Stat label="Documents" value={data.counts.documents} icon={FileText} to="/documents" />
        <Stat label="Published" value={data.counts.published} icon={CheckCircle2} tone="success" to="/documents?status=published" />
        <Stat label="Drafts" value={data.counts.drafts} icon={PencilLine} to="/documents?status=draft" />
        <Stat label="In review" value={data.counts.inReview} icon={GitPullRequest} tone="warning" to="/documents?status=in_review" />
        <Stat label="Open change requests" value={data.counts.openChangeRequests} icon={GitPullRequest} tone="brand" to="/change-requests" />
        <Stat label="Overdue reviews" value={data.counts.overdueReviews} icon={AlertTriangle} tone={data.counts.overdueReviews ? 'danger' : 'neutral'} to="/documents?sort=review&order=asc" />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2">
          <CardHeader
            title="Awaiting your review"
            description="Change requests where you are named as a reviewer."
            actions={
              <Button size="sm" onClick={() => navigate('/change-requests')}>
                View all
              </Button>
            }
          />
          {data.awaitingMyReview.length === 0 ? (
            <EmptyState icon={<CheckCircle2 className="size-8" />} title="Nothing waiting on you" description="You are all caught up." />
          ) : (
            <ul className="divide-y divide-ink-100">
              {data.awaitingMyReview.map((cr) => (
                <li key={cr.id}>
                  <Link to={`/change-requests/${cr.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-3 hover:bg-ink-50">
                    <Badge tone="neutral">#{cr.number}</Badge>
                    <div className="min-w-0 flex-1 basis-40">
                      <p className="truncate text-[13.5px] font-medium text-ink-800">{cr.title}</p>
                      <p className="truncate text-[12px] text-ink-500">
                        {cr.documentReference} · {cr.documentTitle} · raised {fmtRelative(cr.createdAt)}
                      </p>
                    </div>
                    <span className="text-[12px] whitespace-nowrap text-ink-500">
                      {cr.approvals}/{cr.requiredApprovals} approvals
                    </span>
                    <StatusBadge status={cr.state} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="min-w-0">
          <CardHeader title="Reviews due" description="Documents approaching or past their review date." />
          {data.upcomingReviews.length === 0 ? (
            <EmptyState icon={<CalendarClock className="size-8" />} title="No reviews scheduled" />
          ) : (
            <ul className="divide-y divide-ink-100">
              {data.upcomingReviews.map((item) => {
                const days = daysUntil(item.reviewDue);
                const overdue = days !== null && days < 0;
                return (
                  <li key={item.documentId}>
                    <Link to={`/documents/${item.documentId}`} className="flex items-center gap-3 px-5 py-2.5 hover:bg-ink-50">
                      <div className="min-w-0 flex-1 basis-40">
                        <p className="truncate text-[13px] font-medium text-ink-800">{item.title}</p>
                        <p className="text-[11.5px] text-ink-500">{item.reference}</p>
                      </div>
                      <Badge tone={overdue ? 'danger' : days !== null && days < 30 ? 'warning' : 'neutral'}>
                        {overdue ? `${Math.abs(days!)}d overdue` : fmtDate(item.reviewDue)}
                      </Badge>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2">
          <CardHeader title="Recently updated" actions={<Button size="sm" onClick={() => navigate('/documents')}>All documents</Button>} />
          {data.recentDocuments.length === 0 ? (
            <EmptyState
              icon={<FileText className="size-8" />}
              title="No documents yet"
              description="Create your first policy from a template."
              action={can('document:create') ? <Button variant="primary" onClick={() => navigate('/documents?new=1')}>New document</Button> : undefined}
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {data.recentDocuments.map((doc) => (
                <li key={doc.id}>
                  <Link to={`/documents/${doc.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-3 hover:bg-ink-50">
                    <Badge tone="neutral">{doc.reference}</Badge>
                    <div className="min-w-0 flex-1 basis-40">
                      <p className="truncate text-[13.5px] font-medium text-ink-800">{doc.title}</p>
                      <p className="truncate text-[12px] text-ink-500">
                        {doc.documentTypeName} · updated {fmtRelative(doc.updatedAt)}
                        {doc.currentVersion ? ` · ${doc.currentVersion.label}` : ''}
                      </p>
                    </div>
                    <StatusBadge status={doc.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="min-w-0">
          <CardHeader title="Library by type" />
          {data.byType.length === 0 ? (
            <EmptyState title="Nothing to show yet" />
          ) : (
            <ul className="space-y-3 px-5 py-4">
              {data.byType.map((row) => {
                const max = Math.max(...data.byType.map((r) => r.count), 1);
                return (
                  <li key={row.key}>
                    <div className="mb-1 flex items-baseline justify-between text-[12.5px]">
                      <span className="text-ink-700">{row.name}</span>
                      <span className="font-semibold text-ink-900">{row.count}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-ink-100">
                      <div className="h-full rounded-full bg-brand-500" style={{ width: `${(row.count / max) * 100}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
